import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  GraduationCap, UserPlus, Search, Loader2, Plus, Trash2, BookOpen,
  CheckCircle2, UserCheck, IndianRupee, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const SOURCE_TYPES = [
  { v: "neodove", label: "NeoDove" },
  { v: "facebook", label: "Facebook" },
  { v: "student_referral", label: "Student Referral" },
  { v: "franchisee_referral", label: "Franchisee Referral" },
  { v: "direct_website", label: "Direct — Website" },
  { v: "direct_call", label: "Direct — Call" },
  { v: "walk_in", label: "Walk-in" },
  { v: "other", label: "Other" },
];

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const emptyCourseRow = () => ({ course_id: "", batch_id: "" });

export default function NewEnrollmentDialog({ open, onOpenChange, onEnrolled }) {
  const { user } = useAuth();
  const canForOthers = user?.role === "admin" || user?.role === "manager"
    || user?.permissions?.enrollments?.on_behalf === true;

  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [franchisees, setFranchisees] = useState([]);

  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState({ students: [], leads: [] });
  const [target, setTarget] = useState(null); // {type, id, name, mobile, ...}
  const [showNew, setShowNew] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: "", mobile: "", email: "", city: "" });
  const [sourceType, setSourceType] = useState("direct_call");
  const [referringFranchiseeId, setReferringFranchiseeId] = useState("");
  const [referrerStudentId, setReferrerStudentId] = useState("");
  const [referrerStudent, setReferrerStudent] = useState(null);
  const [refQ, setRefQ] = useState("");
  const [refResults, setRefResults] = useState([]);
  const [refSearching, setRefSearching] = useState(false);
  const refDebTimer = useRef(null);

  const [courseRows, setCourseRows] = useState([emptyCourseRow()]);
  const [comboTotal, setComboTotal] = useState("");
  const [comboPaid, setComboPaid] = useState("");
  const [converterId, setConverterId] = useState("");
  const [rmId, setRmId] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const [saving, setSaving] = useState(false);
  const debTimer = useRef(null);

  useEffect(() => {
    if (!open) return;
    // reset
    setQ(""); setResults({ students: [], leads: [] }); setTarget(null);
    setShowNew(false); setNewStudent({ name: "", mobile: "", email: "", city: "" });
    setSourceType("direct_call"); setReferringFranchiseeId("");
    setReferrerStudentId(""); setReferrerStudent(null); setRefQ(""); setRefResults([]);
    setCourseRows([emptyCourseRow()]); setConverterId(user?.id || ""); setRmId("");
    setComboTotal(""); setComboPaid(""); setStudentCode("");
    Promise.all([
      api.get("/courses"), api.get("/batches"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/franchisees").catch(() => ({ data: [] })),
    ]).then(([c, b, u, f]) => {
      setCourses(c.data || []); setBatches(b.data || []);
      setEmployees(u.data || []); setFranchisees(f.data || []);
    }).catch(() => {});
  }, [open]); // eslint-disable-line

  // debounced lookup
  useEffect(() => {
    if (!open) return;
    if (debTimer.current) clearTimeout(debTimer.current);
    if ((q || "").trim().length < 2) { setResults({ students: [], leads: [] }); return; }
    debTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/enrollments/lookup", { params: { q: q.trim() } });
        setResults(data || { students: [], leads: [] });
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 350);
    return () => debTimer.current && clearTimeout(debTimer.current);
  }, [q, open]);

  // debounced lookup for the "referred by student" picker (student_referral)
  useEffect(() => {
    if (!open || sourceType !== "student_referral") return;
    if (refDebTimer.current) clearTimeout(refDebTimer.current);
    if ((refQ || "").trim().length < 2) { setRefResults([]); return; }
    refDebTimer.current = setTimeout(async () => {
      setRefSearching(true);
      try {
        const { data } = await api.get("/enrollments/lookup", { params: { q: refQ.trim() } });
        setRefResults(data?.students || []);
      } catch { /* ignore */ } finally { setRefSearching(false); }
    }, 350);
    return () => refDebTimer.current && clearTimeout(refDebTimer.current);
  }, [refQ, open, sourceType]);

  const pickStudent = (s) => { setTarget({ type: "student", ...s }); setShowNew(false); };
  const pickLead = (l) => { setTarget({ type: "lead", ...l }); setShowNew(false); };
  const startNew = () => {
    setTarget(null); setShowNew(true);
    setNewStudent((p) => ({ ...p, name: q && !/^\d+$/.test(q) ? q : p.name, mobile: /^\d{6,}$/.test(q) ? q : p.mobile }));
  };

  const updateRow = (i, patch) => setCourseRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setCourseRows((r) => [...r, emptyCourseRow()]);
  const removeRow = (i) => setCourseRows((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));

  const validCourseRows = courseRows.filter((r) => r.course_id);
  const isFranchiseeReferral = showNew && sourceType === "franchisee_referral";
  const targetReady = !!target || (showNew && newStudent.name.trim() && newStudent.mobile.trim());
  const newStudentSourceOk = !showNew
    || (!(sourceType === "franchisee_referral" && !referringFranchiseeId)
        && !(sourceType === "student_referral" && !referrerStudentId));
  // Student code is the primary identity — mandatory for new students and for any
  // existing student/lead that doesn't have one yet.
  const targetHasCode = !!(target && (target.student_code || "").trim());
  const needsCode = showNew || (!!target && !targetHasCode);
  // Prefix: PVRS only when ALL selected courses are recorded; else PVS (live/offline).
  const selectedCourseObjs = validCourseRows.map((r) => courses.find((c) => c.id === r.course_id)).filter(Boolean);
  const allRecorded = selectedCourseObjs.length > 0 && selectedCourseObjs.every((c) => c.is_recorded);
  const typeOfCourse = (c) => c?.course_type || (c?.is_recorded ? "recorded" : "live_online");
  const isLiveRow = (r) => typeOfCourse(courses.find((c) => c.id === r.course_id)) === "live_online";
  // Live (online) courses MUST have a batch on every new enrollment.
  const liveRowsMissingBatch = validCourseRows.some((r) => isLiveRow(r) && !r.batch_id);
  const suggestCode = async () => {
    try {
      const { data } = await api.get("/students/next-code", { params: { recorded: allRecorded } });
      setStudentCode(data.code);
    } catch { toast.error("Could not suggest a code"); }
  };
  const canSubmit = targetReady && validCourseRows.length > 0 && newStudentSourceOk
    && (!needsCode || studentCode.trim()) && !liveRowsMissingBatch;

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      const isCombo = validCourseRows.length > 1;
      const totalNum = comboTotal !== "" ? Number(comboTotal) : null;
      const paidNum = comboPaid !== "" ? Number(comboPaid) : 0;
      const body = {
        converted_by_employee_id: converterId || null,
        relationship_manager_id: rmId || null,
      };
      if (isCombo) {
        // ONE combined enrollment: a single combo price + one incentive; each
        // course keeps its own batch.
        body.combined = true;
        body.total_amount = totalNum;
        body.amount_paid = paidNum;
        body.courses = validCourseRows.map((r) => ({ course_id: r.course_id, batch_id: r.batch_id || null }));
      } else {
        // Single course — amount lives on that one enrollment row.
        body.combined = false;
        body.courses = validCourseRows.map((r) => ({
          course_id: r.course_id,
          batch_id: r.batch_id || null,
          total_amount: totalNum,
          amount_paid: paidNum,
        }));
      }
      if (target?.type === "student") { body.mode = "existing_student"; body.student_id = target.id; if (needsCode) body.student_code = studentCode.trim(); }
      else if (target?.type === "lead") { body.mode = "lead"; body.lead_id = target.id; if (needsCode) body.student_code = studentCode.trim(); }
      else {
        body.mode = "new";
        body.source_type = sourceType;
        body.referring_franchisee_id = referringFranchiseeId || null;
        body.new_student = {
          name: newStudent.name.trim(), mobile: newStudent.mobile.trim(),
          email: newStudent.email.trim() || null, city: newStudent.city.trim() || null,
          student_code: studentCode.trim(),
          referrer_student_id: sourceType === "student_referral" ? (referrerStudentId || null) : null,
        };
      }
      const { data } = await api.post("/enrollments/quick", body);
      const dupCount = (data.duplicates_skipped || []).length;
      if ((data.enrollments_created || []).length === 0 && dupCount > 0) {
        toast.warning(`${data.student_name} already enrolled in this course/batch — duplicate skipped.`);
      } else {
        toast.success(
          `${data.student_name} enrolled in ${data.enrollments_created.length} course(s)`
          + (data.is_new_student ? " · new student created" : "")
          + (data.lead_converted ? " · lead converted" : "")
          + (data.incentives_created ? ` · ${data.incentives_created} incentive(s)` : "")
          + (dupCount ? ` · ${dupCount} duplicate skipped` : "")
        );
      }
      onOpenChange(false);
      onEnrolled?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const courseName = (id) => courses.find((c) => c.id === id)?.name || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-enrollment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-primary" /> New Enrollment
          </DialogTitle>
          <DialogDescription className="text-xs">
            Search an existing student or lead, or create a fresh student — then enroll in one or more courses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ── Person selection ── */}
          {!target && !showNew && (
            <div className="space-y-2">
              <Label className="text-xs">Find by name / mobile / email</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Type at least 2 characters…"
                  className="pl-9"
                  data-testid="enroll-search-input"
                  autoFocus
                />
                {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
              </div>

              {(results.students.length > 0 || results.leads.length > 0) && (
                <div className="border border-border rounded-md divide-y divide-border max-h-64 overflow-y-auto">
                  {results.students.map((s) => (
                    <button
                      key={`s-${s.id}`} type="button" onClick={() => pickStudent(s)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-2"
                      data-testid={`enroll-student-result-${s.id}`}
                    >
                      <Badge variant="secondary" className="text-[10px] shrink-0">Student</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{s.name} <span className="text-xs text-muted-foreground">{s.mobile}</span></div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {s.student_code ? `${s.student_code} · ` : ""}{s.enrollment_count} enrollment(s){s.rm_name ? ` · RM ${s.rm_name}` : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                  {results.leads.map((l) => (
                    <button
                      key={`l-${l.id}`} type="button" onClick={() => pickLead(l)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 flex items-center gap-2"
                      data-testid={`enroll-lead-result-${l.id}`}
                    >
                      <Badge className="text-[10px] shrink-0 bg-amber-500 hover:bg-amber-500">Lead</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{l.name} <span className="text-xs text-muted-foreground">{l.mobile}</span></div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {l.status}{l.assigned_to_name ? ` · assigned to ${l.assigned_to_name}` : " · unassigned"}{l.source ? ` · ${l.source}` : ""}
                          {l.converted ? " · already converted" : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <Button type="button" variant="outline" className="w-full" onClick={startNew} data-testid="enroll-new-student-btn">
                <UserPlus className="h-4 w-4 mr-1.5" /> Not found — create a new student
              </Button>
            </div>
          )}

          {/* ── Chosen target summary ── */}
          {target && (
            <div className="rounded-md border border-primary/40 bg-primary/5 p-3 flex items-center gap-3" data-testid="enroll-target-summary">
              <Badge variant={target.type === "lead" ? "default" : "secondary"} className={target.type === "lead" ? "bg-amber-500 hover:bg-amber-500" : ""}>
                {target.type === "lead" ? "Lead" : "Student"}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{target.name}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" />{target.mobile}
                  {target.type === "lead" && target.assigned_to_name && <span>· was assigned to {target.assigned_to_name}</span>}
                  {target.type === "lead" && <span>· will be converted to a student</span>}
                </div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => { setTarget(null); setQ(""); }} data-testid="enroll-change-target-btn">Change</Button>
            </div>
          )}

          {/* ── Student code for existing student/lead ── */}
          {target && targetHasCode && (
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="enroll-existing-code">
              <Badge variant="outline" className="text-[10px] font-mono">{target.student_code}</Badge> existing student code
            </div>
          )}
          {target && !targetHasCode && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5" data-testid="enroll-need-code">
              <Label className="text-[10px] text-amber-800">This {target.type} has no student code yet — set one * <span className="text-amber-700">({allRecorded ? "PVRS — recorded" : "PVS — live/offline"})</span></Label>
              <div className="flex gap-2 mt-1">
                <Input value={studentCode} onChange={(e) => setStudentCode(e.target.value)} placeholder="e.g. PVS1696" className="h-8 text-xs" data-testid="enroll-student-code" />
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={suggestCode} data-testid="enroll-suggest-code">Suggest</Button>
              </div>
            </div>
          )}

          {/* ── New student fields ── */}
          {showNew && (
            <div className="rounded-md border border-border p-3 space-y-3" data-testid="enroll-new-student-form">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium flex items-center gap-1.5"><UserPlus className="h-4 w-4 text-primary" /> New Student</div>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowNew(false); setQ(""); }}>Back to search</Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Full Name *</Label><Input value={newStudent.name} onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })} className="h-8 text-xs" data-testid="enroll-new-name" /></div>
                <div><Label className="text-[10px]">Mobile *</Label><Input value={newStudent.mobile} onChange={(e) => setNewStudent({ ...newStudent, mobile: e.target.value })} className="h-8 text-xs" data-testid="enroll-new-mobile" /></div>
                <div><Label className="text-[10px]">Email</Label><Input value={newStudent.email} onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })} className="h-8 text-xs" /></div>
                <div><Label className="text-[10px]">City</Label><Input value={newStudent.city} onChange={(e) => setNewStudent({ ...newStudent, city: e.target.value })} className="h-8 text-xs" /></div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Student Code * <span className="text-muted-foreground">({allRecorded ? "PVRS — recorded" : "PVS — live/offline"})</span></Label>
                  <div className="flex gap-2">
                    <Input value={studentCode} onChange={(e) => setStudentCode(e.target.value)} placeholder="e.g. PVS1696" className="h-8 text-xs" data-testid="enroll-student-code" />
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={suggestCode} data-testid="enroll-suggest-code">Suggest</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px]">Source</Label>
                  <Select value={sourceType} onValueChange={setSourceType}>
                    <SelectTrigger className="h-8 text-xs" data-testid="enroll-new-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOURCE_TYPES.map((s) => (<SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {sourceType === "franchisee_referral" && (
                  <div>
                    <Label className="text-[10px]">Referring Franchisee *</Label>
                    <Select value={referringFranchiseeId} onValueChange={setReferringFranchiseeId}>
                      <SelectTrigger className="h-8 text-xs" data-testid="enroll-new-franchisee"><SelectValue placeholder="Pick" /></SelectTrigger>
                      <SelectContent className="max-h-60">{franchisees.map((f) => (<SelectItem key={f.id} value={f.id}>{f.name} ({f.city})</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                )}
                {sourceType === "student_referral" && (
                  <div className="col-span-2">
                    <Label className="text-[10px]">Referred by (existing student) *</Label>
                    {referrerStudent ? (
                      <div className="flex items-center justify-between gap-2 h-8 px-2 rounded-md border border-primary/40 bg-primary/5 text-xs" data-testid="enroll-referrer-selected">
                        <span className="truncate flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5 text-primary shrink-0" />{referrerStudent.name} · {referrerStudent.mobile}</span>
                        <button type="button" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => { setReferrerStudent(null); setReferrerStudentId(""); setRefQ(""); setRefResults([]); }} data-testid="enroll-referrer-clear">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input value={refQ} onChange={(e) => setRefQ(e.target.value)} placeholder="Search referring student by name / mobile" className="h-8 text-xs pl-7" data-testid="enroll-referrer-search" />
                        {refQ.trim().length >= 2 && (
                          <div className="absolute z-30 mt-1 w-full max-h-44 overflow-y-auto rounded-md border border-border bg-popover shadow-md" data-testid="enroll-referrer-results">
                            {refSearching && <div className="px-2 py-2 text-xs text-muted-foreground">Searching…</div>}
                            {!refSearching && refResults.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">No students found</div>}
                            {refResults.map((s) => (
                              <button key={s.id} type="button" onClick={() => { setReferrerStudent(s); setReferrerStudentId(s.id); setRefResults([]); }} className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted transition-colors" data-testid={`enroll-referrer-opt-${s.id}`}>
                                {s.name} · {s.mobile}{s.city ? ` · ${s.city}` : ""}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Courses ── */}
          {(target || (showNew && newStudent.name)) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-primary" /> Courses to enroll</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addRow} data-testid="enroll-add-course-row"><Plus className="h-3 w-3 mr-1" /> Add course</Button>
              </div>
              {courseRows.map((row, i) => {
                const rowBatches = batches.filter((b) => b.course_id === row.course_id);
                return (
                  <div key={i} className="rounded-md border border-border p-2.5 space-y-2" data-testid={`enroll-course-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Select value={row.course_id} onValueChange={(v) => updateRow(i, { course_id: v, batch_id: "" })}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid={`enroll-course-select-${i}`}><SelectValue placeholder="Pick a course" /></SelectTrigger>
                        <SelectContent className="max-h-72">{courses.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}{c.price ? ` · ${INR(c.price)}` : ""}</SelectItem>))}</SelectContent>
                      </Select>
                      {courseRows.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeRow(i)} data-testid={`enroll-remove-row-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      )}
                    </div>
                    {row.course_id && (
                      <div>
                        <Label className="text-[10px]">Batch{isLiveRow(row) ? " *" : ""}{validCourseRows.length > 1 ? ` · ${courseName(row.course_id)}` : ""}</Label>
                        <Select value={row.batch_id || "__none__"} onValueChange={(v) => updateRow(i, { batch_id: v === "__none__" ? "" : v })}>
                          <SelectTrigger className={`h-8 text-xs ${isLiveRow(row) && !row.batch_id ? "border-destructive ring-1 ring-destructive/30" : ""}`} data-testid={`enroll-batch-select-${i}`}><SelectValue placeholder={isLiveRow(row) ? "Select a batch (required)" : "No batch"} /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {!isLiveRow(row) && <SelectItem value="__none__">No batch</SelectItem>}
                            {rowBatches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}{b.start_date ? ` · ${b.start_date.slice(0, 10)}` : ""}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        {isLiveRow(row) && !row.batch_id && (
                          <p className="text-[10px] text-destructive mt-0.5" data-testid={`enroll-batch-required-${i}`}>Batch is required for live courses.</p>
                        )}
                        {isLiveRow(row) && rowBatches.length === 0 && (
                          <p className="text-[10px] text-amber-600 mt-0.5">No batches exist for this course yet — create one in Courses → Manage Batches.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Single combo amount (entered ONCE for all selected courses) ── */}
              {validCourseRows.length > 0 && (
                <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 space-y-2" data-testid="enroll-combo-amount">
                  {validCourseRows.length > 1 && (
                    <div className="text-[11px] text-primary font-medium flex items-center gap-1.5">
                      <IndianRupee className="h-3 w-3" /> Combo amount — fill once for all {validCourseRows.length} courses
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">{validCourseRows.length > 1 ? "Combo Total ₹" : "Total ₹"}</Label>
                      <Input type="number" min="0" value={comboTotal} onChange={(e) => setComboTotal(e.target.value)} className="h-8 text-xs" placeholder={validCourseRows.length === 1 ? (courses.find((c) => c.id === validCourseRows[0].course_id)?.price || "0") : "0"} data-testid="enroll-combo-total" />
                    </div>
                    <div>
                      <Label className="text-[10px]">Paid now ₹</Label>
                      <Input type="number" min="0" value={comboPaid} onChange={(e) => setComboPaid(e.target.value)} className="h-8 text-xs" placeholder="0" data-testid="enroll-combo-paid" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Converter / RM ── */}
          {(target || (showNew && newStudent.name)) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <UserCheck className="h-3 w-3" />
                  {isFranchiseeReferral ? "Processed by (no incentive — franchisee earns)" : "Converted by (earns incentive)"}
                </Label>
                {canForOthers ? (
                  <Select value={converterId} onValueChange={setConverterId}>
                    <SelectTrigger className="h-8 text-xs" data-testid="enroll-converter-select"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {employees.filter((e) => e.role !== "admin" && e.active !== false).map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}{e.employee_code ? ` · ${e.employee_code}` : ""}</SelectItem>))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="h-8 flex items-center text-xs text-muted-foreground border border-border rounded-md px-2.5">
                    {isFranchiseeReferral ? `Recorded as you (${user?.name}) — no incentive` : `Credited to you (${user?.name})`}
                  </div>
                )}
                {isFranchiseeReferral && (
                  <p className="text-[10px] text-amber-700 mt-1">Incentive franchisee ko jaayega, employee ko nahi.</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Relationship Manager (optional)</Label>
                <Select value={rmId || "__none__"} onValueChange={(v) => setRmId(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="enroll-rm-select"><SelectValue placeholder="Same as converter" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">Same as converter</SelectItem>
                    {employees.filter((e) => e.role !== "admin" && e.active !== false).map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}{e.employee_code ? ` · ${e.employee_code}` : ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {validCourseRows.length > 0 && targetReady && (
            <div className="text-[11px] text-muted-foreground rounded-md bg-muted/40 border border-border p-2">
              {validCourseRows.length > 1 ? (
                <>Will create <b>1 combined enrollment</b> of {validCourseRows.length} courses ({validCourseRows.map((r) => courseName(r.course_id)).filter(Boolean).join(" + ")}) at one combo price. {isFranchiseeReferral ? "Fires a single franchisee incentive (HR fills amount) — employee gets none." : "Fires a single incentive (HR fills amount)."}</>
              ) : (
                <>Will create <b>1</b> enrollment: {validCourseRows.map((r) => courseName(r.course_id)).filter(Boolean).join(", ")}. {isFranchiseeReferral ? "Fires a franchisee incentive (HR fills amount) — employee gets none." : "Fires its own incentive (HR fills amount)."}</>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || saving} className="bg-primary hover:bg-primary/90" data-testid="enroll-submit-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
            {saving ? "Enrolling…" : "Create Enrollment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
